import axios from "axios";
import { getAccessTokenOrRefresh } from "@/lib/tokenFactory";
import { notifyError } from "../notify";
// Adjust this import path if notify.js lives elsewhere

const REQUEST_TIMEOUT_MS = 15000;
const RETRY_DELAY_MS = 250;
const RETRYABLE_METHODS = new Set(["get", "head", "options"]);
const RETRYABLE_STATUSES = new Set([502, 503, 504]);

const clientOptions = {
  baseURL: "/api",
  timeout: REQUEST_TIMEOUT_MS,
  headers: { "Content-Type": "application/x-www-form-urlencoded" },
};

export const plainAxios = axios.create({
  ...clientOptions,
});

export const api = axios.create({
  ...clientOptions,
});

const attachRetryInterceptor = (client) => {
  client.interceptors.response.use(undefined, async (error) => {
    const config = error?.config;
    const method = config?.method?.toLowerCase();
    const retryable =
      config?.idempotent === true || RETRYABLE_METHODS.has(method);
    const transient =
      !error?.response || RETRYABLE_STATUSES.has(error.response.status);

    if (
      !config ||
      !retryable ||
      !transient ||
      error.code === "ERR_CANCELED" ||
      (config.retryCount ?? 0) >= 1
    ) {
      return Promise.reject(error);
    }

    config.retryCount = (config.retryCount ?? 0) + 1;
    await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY_MS));
    return client.request(config);
  });
};

attachRetryInterceptor(plainAxios);
attachRetryInterceptor(api);

api.interceptors.request.use(async (config) => {
  if (!config.headers?.Authorization) {
    const token = await getAccessTokenOrRefresh();
    if (token) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${token}`;
    }
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error?.response?.status === 403) {
      notifyError("components.permissions.no_permission_notification");
    }
    return Promise.reject(error);
  }
);

export default api;
